import React from "react";

import VacancyContractStatusHistory from "./components/history/VacancyContractStatusHistory";
import VacancyContractAttachment from "./components/contract-attachment/VacancyContractAttachment";
import i18n from "@/i18n";
import { useVacancyContractData } from "./components/details/useVacancyContractData";
import { APActivity } from "@/apps/tatar/activityApp/ActivityInterfaces";
import VacancyContractStatusBox from "./components/details/VacancyContractStatusBox";
import { useActivityConstants } from "@/apps/tatar/activityApp/ActivityHooks";
import {
  APOfferVacancyContractStatus,
  APOfferVacancyMeta,
} from "../model/VacancyApplicant.interface";
import VacancyApplicantListEntry from "../vacancy-applicant/components/vacancy-applicant-entry/VacancyApplicantListEntry";
import useVacancyRentalAgreement from "./components/details/useVacancyRentalAgreement";
import StructLoader from "@/components/StructLoader/StructLoader";
import { ActivityAbstractStructClass } from "@/redux/actions/struct/implemented/ActivityAbstractStruct";
import VacancyOfferUtils from "../vacancy-applicant/utils/VacancyOfferUtils";
import VacancyContractActionLog, {
  VacancyContractActionLogEntry,
} from "./components/action-log/VacancyContractActionLog";
import BFButton from "@/modules/abstract-ui/general/Button/BFButton";
import ModalManager from "@/components/ModalComponent/ModalManager";
import { ActivityStructContext } from "../../../ActivityStructContext";
import useVacancyContractDetailsActions from "./components/details/useVacancyContractDetailsActions";

type Props = {
  activity: APActivity;
  activityStruct: ActivityAbstractStructClass<any>;
};

const VacancyContractDetails = (props: Props) => {
  const { activity, activityStruct } = props;

  const constants = useActivityConstants();

  const { offer, application, reload } = useVacancyContractData(activity);
  const { openContractCreationModal, openContractSigningModal } =
    useVacancyContractDetailsActions();

  const { createRentalAgreement } = useVacancyRentalAgreement({
    activity,
    offer,
    activityStruct,
    reload,
  });

  if (!offer) {
    return (
      <div className="padding-20">
        {i18n.t(
          "apTemplate:Vacancy.Applicant.ContractDetails.Label.OfferNotFound",
          "Es wurde noch kein Mietinteressent genehmigt."
        )}
      </div>
    );
  }

  const offerMeta = offer.data.meta as APOfferVacancyMeta;

  const status = offer.data.status as APOfferVacancyContractStatus;
  const hasAttachments = Boolean(offerMeta.contracts?.length);

  const logStatus = VacancyOfferUtils.getOfferStatusForContractLog();

  const actionLog = offerMeta.actionLog || [];
  const lastLogEntry = actionLog?.[actionLog.length - 1] || null;
  const showLog = logStatus.includes(status);

  return (
    <StructLoader
      structTypes={["objectKind", "orga", "vacancy"]}
      unitType={activity.data.type}
      render={([_objectKind, _orga, vacancyStruct]) => {
        return (
          <ActivityStructContext.Provider value={vacancyStruct}>
            <div className="__card padding-20">
              <div className="__h3 __flex">
                {i18n.t(
                  "apTemplate:Vacancy.Applicant.ContractDetails.Label.OfferStatusTitle",
                  "Status Mietvertrag"
                )}
              </div>
              <VacancyContractStatusHistory offer={offer} />

              <VacancyContractStatusBox
                offer={offer}
                activity={activity}
                onEdit={() =>
                  openContractCreationModal({
                    application,
                    activity,
                    offer,
                    reload,
                  })
                }
                onSign={() =>
                  openContractSigningModal({ activity, offer, reload })
                }
                onCreateRentalAgreement={() => {
                  createRentalAgreement(application);
                }}
                reload={reload}
              />
              <div className="__grid __grid-cols-3 __grid-gap-10 __justify-items-start padding-top-10 __align-items-start">
                {showLog && (
                  <div>
                    <div className="padding-top-10 __flex">
                      <div className="__h3">
                        {i18n.t(
                          "apTemplate:Vacancy.Applicant.ContractDetails.Label.Log",
                          "Protokoll"
                        )}
                      </div>
                      {lastLogEntry && (
                        <BFButton
                          size="xs"
                          appearance="link"
                          onClick={() => {
                            ModalManager.show({
                              title: i18n.t(
                                "apTemplate:Vacancy.Applicant.ContractDetails.Label.Log",
                                "Protokoll"
                              ),
                              content: () => (
                                <VacancyContractActionLog offer={offer} />
                              ),
                              size: "fluid",
                              headerCloseButton: true,
                              buttons: [
                                {
                                  text: i18n.t(
                                    "Globals.Buttons.Close",
                                    "Schließen"
                                  ),
                                  appearance: "link",
                                  closeOnClick: true,
                                },
                              ],
                            });
                          }}
                        >
                          {i18n.t("Globals.Open", "Öffnen")}
                        </BFButton>
                      )}
                    </div>
                    {lastLogEntry ? (
                      <div className="padding-top-10">
                        <div className="__sub-text padding-bottom-6">
                          {i18n.t(
                            "apTemplate:Vacancy.Applicant.ContractDetails.Label.LastLog",
                            "Letzter Protokolleintrag"
                          )}
                        </div>
                        <VacancyContractActionLogEntry
                          actionLogEntry={lastLogEntry}
                        />
                      </div>
                    ) : (
                      <div className="padding-top-10 __sub-text">
                        {i18n.t(
                          "apTemplate:Vacancy.Applicant.ContractDetails.Label.NoLog",
                          "Es gibt noch keine Protokolleinträge."
                        )}
                      </div>
                    )}
                  </div>
                )}
                {hasAttachments && (
                  <div>
                    <div className="__h3 padding-top-10">
                      {i18n.t(
                        "apTemplate:Vacancy.Applicant.ContractDetails.Label.Attachments",
                        "Anhang"
                      )}
                    </div>
                    <div className="padding-top-10">
                      <VacancyContractAttachment offer={offer} />
                    </div>
                  </div>
                )}
              </div>

              <div className="padding-top-10">
                <VacancyApplicantListEntry
                  activity={activity}
                  offer={offer}
                  editable={false}
                  hideWfStatus
                  constants={constants}
                />
              </div>
            </div>
          </ActivityStructContext.Provider>
        );
      }}
    />
  );
};

export default VacancyContractDetails;
